import clsx from 'clsx';
import { ComponentPropsWithoutRef, ElementType, ReactNode } from 'react';

type ParagraphSize = 'xl' | 'l' | 'm' | 's';
type ParagraphWeight = 'normal' | 'medium' | 'bold';

type Props<T extends ElementType> = {
  as?: T;
  size?: ParagraphSize;
  weight?: ParagraphWeight;
  children: ReactNode;
  dataTestId?: string;
  /**
   * Based on Tailwind colors
   * @see https://tailwindcss.com/docs/colors
   */
  color?: string;
} & Omit<ComponentPropsWithoutRef<T>, 'data-testid'>;

const weightStyles: Record<ParagraphWeight, string> = {
  normal: 'font-normal',
  medium: 'font-medium',
  bold: 'font-semibold',
};

const sizeStyles: Record<ParagraphSize, string> = {
  xl: 'text-lg leading-6',
  l: 'text-base',
  m: 'text-sm',
  s: 'text-xs',
};

export const Paragraph = <T extends ElementType = 'p'>({
  as,
  size = 'm',
  weight = 'normal',
  children,
  dataTestId,
  color,
  ...props
}: Props<T>) => {
  const Component = as || 'p';

  return (
    <Component
      {...props}
      className={clsx(
        color || 'text-gray-900',
        weightStyles[weight],
        sizeStyles[size],
        props.className,
      )}
      data-testid={dataTestId}
    >
      {children}
    </Component>
  );
};
